import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { billsAPI, visitsAPI, patientsAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Receipt, DollarSign, Printer, Eye, CreditCard, Banknote, Building, Smartphone, Clock, CheckCircle, AlertCircle, FileSignature, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Check, Pencil } from 'lucide-react';
import ViewConsentDialog from '../components/ViewConsentDialog';
import DataPagination from '../components/DataPagination';

import { useAuth } from '../contexts/AuthContext';

const Billing = () => {
  const { workingLocation, clinic, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'pending');
  const [openAccordionId, setOpenAccordionId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Track which tabs have been loaded (for lazy loading)
  const [tabsLoaded, setTabsLoaded] = useState({
    pending: false,
    partially_paid: false,
    paid: false
  });
  
  // Refs to prevent duplicate API calls
  const fetchingPendingRef = useRef(false);
  const fetchingPartiallyPaidRef = useRef(false);
  const fetchingPaidRef = useRef(false);
  const findingBillRef = useRef(false);
  
  // Pending bills state and pagination
  const [pendingBills, setPendingBills] = useState([]);
  const [pendingCurrentPage, setPendingCurrentPage] = useState(1);
  const [pendingPageSize, setPendingPageSize] = useState(20);
  const [pendingTotalItems, setPendingTotalItems] = useState(0);
  const [pendingTotalPages, setPendingTotalPages] = useState(0);
  const [loadingPending, setLoadingPending] = useState(false);
  
  // Partially paid bills state and pagination
  const [partiallyPaidBills, setPartiallyPaidBills] = useState([]);
  const [partiallyPaidCurrentPage, setPartiallyPaidCurrentPage] = useState(1);
  const [partiallyPaidPageSize, setPartiallyPaidPageSize] = useState(20);
  const [partiallyPaidTotalItems, setPartiallyPaidTotalItems] = useState(0);
  const [partiallyPaidTotalPages, setPartiallyPaidTotalPages] = useState(0);
  const [loadingPartiallyPaid, setLoadingPartiallyPaid] = useState(false);
  
  // Paid bills state and pagination
  const [paidBillsData, setPaidBillsData] = useState([]);
  const [paidCurrentPage, setPaidCurrentPage] = useState(1);
  const [paidPageSize, setPaidPageSize] = useState(20);
  const [paidTotalItems, setPaidTotalItems] = useState(0);
  const [paidTotalPages, setPaidTotalPages] = useState(0);
  const [loadingPaid, setLoadingPaid] = useState(false);
  
  // Legacy state for backwards compatibility (maps to pending)
  const bills = pendingBills;
  const setBills = setPendingBills;
  const currentPage = pendingCurrentPage;
  const setCurrentPage = setPendingCurrentPage;
  const pageSize = pendingPageSize;
  const setPageSize = setPendingPageSize;
  const totalItems = pendingTotalItems;
  const setTotalItems = setPendingTotalItems;
  const totalPages = pendingTotalPages;
  const setTotalPages = setPendingTotalPages;
  
  // Dialog states
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [consentDialogOpen, setConsentDialogOpen] = useState(false);
  const [editBillDialogOpen, setEditBillDialogOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [selectedConsent, setSelectedConsent] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  
  // Payment form with split payment support
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    method: 'cash',
    reference: '',
    discount: 0,
    // Split payment support
    splitPayment: false,
    cashAmount: 0,
    cardAmount: 0,
    zelleAmount: 0
  });

  // Editable items for discount
  const [editableServices, setEditableServices] = useState([]);
  const [editableMedications, setEditableMedications] = useState([]);
  const [googleReviewDiscount, setGoogleReviewDiscount] = useState(false);
  
  // Edit bill state
  const [editBillForm, setEditBillForm] = useState({
    services: [],
    medications: []
  });
  const [editBillLoading, setEditBillLoading] = useState(false);

  // Initial load - only fetch pending bills
  useEffect(() => {
    fetchPendingBills();
  }, [workingLocation]);

  // Refetch pending bills when pagination changes (only if tab was loaded)
  useEffect(() => {
    if (findingBillRef.current) return;
    if (tabsLoaded.pending) {
      fetchPendingBills(true);
    }
  }, [pendingCurrentPage, pendingPageSize, searchQuery]);

  // Refetch partially paid bills when pagination changes (only if tab was loaded)
  useEffect(() => {
    if (findingBillRef.current) return;
    if (tabsLoaded.partially_paid && activeTab === 'partially_paid') {
      fetchPartiallyPaidBills(true);
    }
  }, [partiallyPaidCurrentPage, partiallyPaidPageSize, searchQuery]);

  // Refetch paid bills when pagination changes (only if tab was loaded)
  useEffect(() => {
    if (findingBillRef.current) return;
    if (tabsLoaded.paid && activeTab === 'paid') {
      fetchPaidBills(true);
    }
  }, [paidCurrentPage, paidPageSize, searchQuery]);

  // Check if user can edit bills (only doctors and admins)
  const canEditBills = user?.role === 'doctor' || user?.role === 'clinic_admin' || user?.role === 'super_admin' || user?.role === 'admin';
  
  // Check if user can apply discounts (doctors and admins only, NOT front desk)
  const canApplyDiscount = user?.role === 'doctor' || user?.role === 'clinic_admin' || user?.role === 'super_admin' || user?.role === 'admin';

  // Front desk + admins may correct a payment method, but only for payments collected TODAY
  const canEditPaymentMethod = user?.role === 'front_desk' || user?.role === 'clinic_admin' || user?.role === 'super_admin' || user?.role === 'admin';
  const [editingPayment, setEditingPayment] = useState(null); // `${billId}:${idx}` currently being edited
  const isCollectedToday = (iso) => {
    if (!iso) return false;
    const d = new Date(iso);
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  };
  const handleUpdatePaymentMethod = async (billId, idx, method) => {
    try {
      await billsAPI.updatePaymentMethod(billId, idx, method);
      toast.success('Payment method updated');
      setEditingPayment(null);
      fetchPendingBills(true);
      fetchPartiallyPaidBills(true);
      fetchPaidBills(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update payment method');
    }
  };

  // Handle URL params for direct navigation and auto-open accordion
  useEffect(() => {
    const tab = searchParams.get('tab');
    const billId = searchParams.get('bill');
    if (tab) {
      setActiveTab(tab);
      // Trigger lazy load for the tab
      handleTabChange(tab);
    }
    if (billId) {
      // Search for the bill and navigate to it
      findAndOpenBill(billId);
    }
  }, [searchParams]);

  // Find a bill by ID across all tabs and open it
  const findAndOpenBill = async (billId) => {
    findingBillRef.current = true;
    try {
      // First get the bill directly to determine its status
      const response = await billsAPI.getById(billId);
      const bill = response.data;
      
      if (!bill) {
        toast.error('Bill not found');
        return;
      }
      
      // Determine which tab this bill should be in
      const targetTab = bill.status === 'paid' ? 'paid' 
                      : bill.status === 'partially_paid' ? 'partially_paid' 
                      : 'pending';
      
      // Use last 8 chars of ID as search term (matches billing number display)
      const searchTerm = billId.slice(-8);
      
      // Fetch bills for the correct tab with search, without location filter
      const params = { 
        status: targetTab,
        search: searchTerm,
        page: 1,
        page_size: 50
      };
      
      const billsResponse = await billsAPI.getAll(params);
      const fetchedBills = billsResponse.data.items || [];
      
      // Update the correct tab's state directly
      if (targetTab === 'paid') {
        setPaidBillsData(fetchedBills);
        setPaidTotalItems(billsResponse.data.total || 0);
        setPaidTotalPages(billsResponse.data.total_pages || 0);
        setPaidCurrentPage(1);
        setTabsLoaded(prev => ({ ...prev, paid: true }));
      } else if (targetTab === 'partially_paid') {
        setPartiallyPaidBills(fetchedBills);
        setPartiallyPaidTotalItems(billsResponse.data.total || 0);
        setPartiallyPaidTotalPages(billsResponse.data.total_pages || 0);
        setPartiallyPaidCurrentPage(1);
        setTabsLoaded(prev => ({ ...prev, partially_paid: true }));
      } else {
        setPendingBills(fetchedBills);
        setPendingTotalItems(billsResponse.data.total || 0);
        setPendingTotalPages(billsResponse.data.total_pages || 0);
        setPendingCurrentPage(1);
        setTabsLoaded(prev => ({ ...prev, pending: true }));
      }
      
      // Switch to the correct tab and set search query
      setActiveTab(targetTab);
      setSearchQuery(searchTerm);
      
      // Open the accordion for this bill
      setOpenAccordionId(billId);
      
      // Scroll to the bill after React renders
      setTimeout(() => {
        findingBillRef.current = false;
        const billElement = document.querySelector(`[data-bill-id="${billId}"]`);
        if (billElement) {
          billElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          billElement.style.boxShadow = '0 0 0 3px #3b82f6';
          setTimeout(() => { billElement.style.boxShadow = ''; }, 3000);
        }
      }, 600);
    } catch (error) {
      findingBillRef.current = false;
      console.error('Failed to find bill:', error);
      toast.error('Bill not found');
    }
  };

  // Handle tab change with lazy loading
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'partially_paid' && !tabsLoaded.partially_paid) {
      fetchPartiallyPaidBills();
    } else if (tab === 'paid' && !tabsLoaded.paid) {
      fetchPaidBills();
    }
  };

  // Fetch pending bills
  const fetchPendingBills = async (forceRefresh = false) => {
    if (tabsLoaded.pending && !forceRefresh) return;
    if (fetchingPendingRef.current) return; // Prevent duplicate calls
    
    fetchingPendingRef.current = true;
    try {
      setLoadingPending(true);
      setLoading(true);
      const params = { 
        page: pendingCurrentPage, 
        page_size: pendingPageSize,
        status: 'pending'
      };
      if (workingLocation?.id) {
        params.location_id = workingLocation.id;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      const billsRes = await billsAPI.getAll(params);
      const paginatedData = billsRes.data;
      setPendingBills(paginatedData.items || []);
      setPendingTotalItems(paginatedData.total || 0);
      setPendingTotalPages(paginatedData.total_pages || 0);
      setTabsLoaded(prev => ({ ...prev, pending: true }));
    } catch (error) {
      toast.error('Failed to fetch pending bills');
    } finally {
      setLoadingPending(false);
      setLoading(false);
      fetchingPendingRef.current = false;
    }
  };

  // Fetch partially paid bills
  const fetchPartiallyPaidBills = async (forceRefresh = false) => {
    if (tabsLoaded.partially_paid && !forceRefresh) return;
    if (fetchingPartiallyPaidRef.current) return; // Prevent duplicate calls
    
    fetchingPartiallyPaidRef.current = true;
    try {
      setLoadingPartiallyPaid(true);
      const params = { 
        page: partiallyPaidCurrentPage, 
        page_size: partiallyPaidPageSize,
        status: 'partially_paid'
      };
      if (workingLocation?.id) {
        params.location_id = workingLocation.id;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      const billsRes = await billsAPI.getAll(params);
      const paginatedData = billsRes.data;
      setPartiallyPaidBills(paginatedData.items || []);
      setPartiallyPaidTotalItems(paginatedData.total || 0);
      setPartiallyPaidTotalPages(paginatedData.total_pages || 0);
      setTabsLoaded(prev => ({ ...prev, partially_paid: true }));
    } catch (error) {
      console.error('Failed to fetch partially paid bills:', error);
    } finally {
      setLoadingPartiallyPaid(false);
      fetchingPartiallyPaidRef.current = false;
    }
  };

  // Fetch paid bills
  const fetchPaidBills = async (forceRefresh = false) => {
    if (tabsLoaded.paid && !forceRefresh) return;
    if (fetchingPaidRef.current) return; // Prevent duplicate calls
    
    fetchingPaidRef.current = true;
    try {
      setLoadingPaid(true);
      const params = { 
        page: paidCurrentPage, 
        page_size: paidPageSize,
        status: 'paid'
      };
      if (workingLocation?.id) {
        params.location_id = workingLocation.id;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      const billsRes = await billsAPI.getAll(params);
      const paginatedData = billsRes.data;
      setPaidBillsData(paginatedData.items || []);
      setPaidTotalItems(paginatedData.total || 0);
      setPaidTotalPages(paginatedData.total_pages || 0);
      setTabsLoaded(prev => ({ ...prev, paid: true }));
    } catch (error) {
      console.error('Failed to fetch paid bills:', error);
    } finally {
      setLoadingPaid(false);
      fetchingPaidRef.current = false;
    }
  };

  // Refresh all loaded tabs (used after payment processing)
  const refreshAllTabs = async () => {
    await fetchPendingBills(true);
    if (tabsLoaded.partially_paid) {
      await fetchPartiallyPaidBills(true);
    }
    if (tabsLoaded.paid) {
      await fetchPaidBills(true);
    }
  };

  // Legacy fetchData function for compatibility
  const fetchData = async () => {
    await fetchPendingBills(true);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const openPaymentDialog = async (bill) => {
    setSelectedBill(bill);
    // Initialize editable items with current discounts
    setEditableServices((bill.services || []).map(s => ({
      ...s,
      discount: s.discount || 0,
      total: s.total || (s.quantity * s.unit_price)
    })));
    setEditableMedications((bill.medications || []).map(m => ({
      ...m,
      discount: m.discount || 0,
      total: m.total || (m.quantity * m.unit_price)
    })));
    setPaymentForm({
      amount: bill.balance_due,
      method: 'cash',
      reference: '',
      discount: 0,
      splitPayment: false,
      cashAmount: 0,
      cardAmount: 0,
      zelleAmount: 0
    });
    
    setPaymentDialogOpen(true);
    setGoogleReviewDiscount(false);
  };

  // Calculate totals with discounts
  const getEditableTotals = () => {
    const servicesTotal = editableServices.reduce((sum, s) => sum + (s.total || 0), 0);
    const medsTotal = editableMedications.reduce((sum, m) => sum + (m.total || 0), 0);
    const totalDiscount = editableServices.reduce((sum, s) => sum + (s.discount || 0), 0) + 
                          editableMedications.reduce((sum, m) => sum + (m.discount || 0), 0);
    const total = servicesTotal + medsTotal;
    const reviewDiscount = googleReviewDiscount ? Math.min(10, total) : 0;
    const alreadyPaid = selectedBill?.amount_paid || 0;
    const balanceDue = Math.max(0, total - reviewDiscount - alreadyPaid);
    return { servicesTotal, medsTotal, totalDiscount, total, alreadyPaid, balanceDue, reviewDiscount };
  };

  const updateServiceDiscount = (index, discount) => {
    const updated = [...editableServices];
    const discountValue = parseFloat(discount) || 0;
    const subtotal = updated[index].quantity * updated[index].unit_price;
    updated[index].discount = Math.min(discountValue, subtotal);
    updated[index].total = subtotal - updated[index].discount;
    setEditableServices(updated);
    // Update payment amount to new balance
    const newTotals = {
      servicesTotal: updated.reduce((sum, s) => sum + (s.total || 0), 0),
      medsTotal: editableMedications.reduce((sum, m) => sum + (m.total || 0), 0)
    };
    const newTotal = newTotals.servicesTotal + newTotals.medsTotal;
    const alreadyPaid = selectedBill?.amount_paid || 0;
    setPaymentForm(prev => ({ ...prev, amount: Math.max(0, newTotal - alreadyPaid) }));
  };

  const updateMedicationDiscount = (index, discount) => {
    const updated = [...editableMedications];
    const discountValue = parseFloat(discount) || 0;
    const subtotal = updated[index].quantity * updated[index].unit_price;
    updated[index].discount = Math.min(discountValue, subtotal);
    updated[index].total = subtotal - updated[index].discount;
    setEditableMedications(updated);
    // Update payment amount to new balance
    const newTotals = {
      servicesTotal: editableServices.reduce((sum, s) => sum + (s.total || 0), 0),
      medsTotal: updated.reduce((sum, m) => sum + (m.total || 0), 0)
    };
    const newTotal = newTotals.servicesTotal + newTotals.medsTotal;
    const alreadyPaid = selectedBill?.amount_paid || 0;
    setPaymentForm(prev => ({ ...prev, amount: Math.max(0, newTotal - alreadyPaid) }));
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    try {
      // Apply Google Review discount to the bill if checked
      if (googleReviewDiscount) {
        const totals = getEditableTotals();
        await billsAPI.update(selectedBill.id, {
          discount: (selectedBill.discount || 0) + totals.reviewDiscount,
          total_amount: totals.total - totals.reviewDiscount,
          balance_due: totals.balanceDue,
          discount_reason: 'Google Review Discount'
        });
      }

      // Process split payments or single payment
      if (paymentForm.splitPayment) {
        // Process split payments - one API call for each method with amount > 0
        const splitPayments = [];
        if (paymentForm.cashAmount > 0) {
          splitPayments.push({ method: 'cash', amount: parseFloat(paymentForm.cashAmount) });
        }
        if (paymentForm.cardAmount > 0) {
          splitPayments.push({ method: 'card', amount: parseFloat(paymentForm.cardAmount) });
        }
        if (paymentForm.zelleAmount > 0) {
          splitPayments.push({ method: 'zelle', amount: parseFloat(paymentForm.zelleAmount) });
        }
        
        // Process each split payment sequentially
        for (const payment of splitPayments) {
          await billsAPI.pay(selectedBill.id, {
            bill_id: selectedBill.id,
            amount: payment.amount,
            method: payment.method,
            reference: paymentForm.reference || null
          });
        }
        toast.success(`Split payment processed: $${getTotalSplitAmount().toFixed(2)}`);
      } else {
        // Process single payment
        await billsAPI.pay(selectedBill.id, {
          bill_id: selectedBill.id,
          amount: parseFloat(paymentForm.amount),
          method: paymentForm.method,
          reference: paymentForm.reference || null
        });
        toast.success('Payment processed successfully');
      }
      
      setPaymentDialogOpen(false);
      refreshAllTabs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payment');
    }
  };
  
  // Handle closing a $0 bill without payment
  const handleCloseBill = async () => {
    try {
      await billsAPI.close(selectedBill.id);
      toast.success('Bill closed successfully');
      setPaymentDialogOpen(false);
      refreshAllTabs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close bill');
    }
  };
  
  // Calculate total split payment amount
  const getTotalSplitAmount = () => {
    return (parseFloat(paymentForm.cashAmount) || 0) + 
           (parseFloat(paymentForm.cardAmount) || 0) + 
           (parseFloat(paymentForm.zelleAmount) || 0);
  };

  const openReceiptDialog = (bill) => {
    setSelectedBill(bill);
    setReceiptDialogOpen(true);
  };

  const viewConsent = async (bill) => {
    try {
      if (!bill.visit_id) {
        toast.error('No visit associated with this bill');
        return;
      }
      const response = await visitsAPI.getById(bill.visit_id);
      const visit = response.data;
      if (visit.consent) {
        setSelectedConsent(visit.consent);
        setSelectedBill(bill);
        setConsentDialogOpen(true);
      } else {
        toast.error('No consent signature found for this visit');
      }
    } catch (error) {
      toast.error('Failed to load consent data');
    }
  };

  // Open edit bill dialog for completed/paid bills
  const openEditBillDialog = (bill) => {
    setSelectedBill(bill);
    setEditBillForm({
      services: (bill.services || []).map(s => ({
        ...s,
        original_quantity: s.quantity,
        unit_price: s.unit_price || 0,
        total: s.total || (s.quantity * (s.unit_price || 0))
      })),
      medications: (bill.medications || []).map(m => ({
        ...m,
        original_quantity: m.quantity,
        unit_price: m.unit_price || 0,
        total: m.total || (m.quantity * (m.unit_price || 0))
      }))
    });
    setEditBillDialogOpen(true);
  };

  // Update service in edit bill form
  const updateEditBillService = (index, field, value) => {
    const updated = [...editBillForm.services];
    if (field === 'quantity') {
      updated[index].quantity = Math.max(0, parseInt(value) || 0);
    } else if (field === 'unit_price') {
      updated[index].unit_price = parseFloat(value) || 0;
    }
    updated[index].total = updated[index].quantity * updated[index].unit_price;
    setEditBillForm({ ...editBillForm, services: updated });
  };

  // Remove service from edit bill
  const removeEditBillService = (index) => {
    const updated = editBillForm.services.filter((_, i) => i !== index);
    setEditBillForm({ ...editBillForm, services: updated });
  };

  // Update medication in edit bill form
  const updateEditBillMedication = (index, field, value) => {
    const updated = [...editBillForm.medications];
    if (field === 'quantity') {
      updated[index].quantity = Math.max(0, parseInt(value) || 0);
    } else if (field === 'unit_price') {
      updated[index].unit_price = parseFloat(value) || 0;
    }
    updated[index].total = updated[index].quantity * updated[index].unit_price;
    setEditBillForm({ ...editBillForm, medications: updated });
  };

  // Remove medication from edit bill
  const removeEditBillMedication = (index) => {
    const updated = editBillForm.medications.filter((_, i) => i !== index);
    setEditBillForm({ ...editBillForm, medications: updated });
  };

  // Calculate edit bill totals
  const getEditBillTotals = () => {
    const servicesTotal = editBillForm.services.reduce((sum, s) => sum + (s.total || 0), 0);
    const medsTotal = editBillForm.medications.reduce((sum, m) => sum + (m.total || 0), 0);
    return { servicesTotal, medsTotal, total: servicesTotal + medsTotal };
  };

  // Save edited bill with inventory sync
  const handleSaveEditedBill = async () => {
    if (!selectedBill) return;
    
    setEditBillLoading(true);
    try {
      // Calculate inventory adjustments needed
      const inventoryAdjustments = [];
      
      // Check medication changes
      const originalMeds = selectedBill.medications || [];
      const newMeds = editBillForm.medications;
      
      // For removed medications - restore inventory
      for (const origMed of originalMeds) {
        const newMed = newMeds.find(m => m.medication_id === origMed.medication_id || m.inventory_item_id === origMed.inventory_item_id);
        if (!newMed) {
          // Medication was removed - restore full quantity
          inventoryAdjustments.push({
            inventory_item_id: origMed.inventory_item_id || origMed.medication_id,
            adjustment: origMed.quantity, // Positive = restore
            reason: `Bill edit: Removed ${origMed.medication_name}`
          });
        } else if (newMed.quantity < origMed.quantity) {
          // Quantity reduced - restore difference
          const diff = origMed.quantity - newMed.quantity;
          inventoryAdjustments.push({
            inventory_item_id: origMed.inventory_item_id || origMed.medication_id,
            adjustment: diff, // Positive = restore
            reason: `Bill edit: Reduced ${origMed.medication_name} by ${diff}`
          });
        } else if (newMed.quantity > origMed.quantity) {
          // Quantity increased - deduct difference
          const diff = newMed.quantity - origMed.quantity;
          inventoryAdjustments.push({
            inventory_item_id: origMed.inventory_item_id || origMed.medication_id,
            adjustment: -diff, // Negative = deduct
            reason: `Bill edit: Increased ${origMed.medication_name} by ${diff}`
          });
        }
      }
      
      // Calculate new totals
      const totals = getEditBillTotals();
      const newAmountPaid = Math.min(selectedBill.amount_paid || 0, totals.total);
      const newBalanceDue = totals.total - newAmountPaid;
      const newStatus = newBalanceDue <= 0 ? 'paid' : (newAmountPaid > 0 ? 'partially_paid' : 'pending');
      
      // Update the bill
      await billsAPI.update(selectedBill.id, {
        services: editBillForm.services,
        medications: editBillForm.medications,
        services_subtotal: totals.servicesTotal,
        medications_subtotal: totals.medsTotal,
        total_amount: totals.total,
        balance_due: newBalanceDue,
        status: newStatus
      });
      
      // Apply inventory adjustments
      for (const adj of inventoryAdjustments) {
        try {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inventory/adjust`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              inventory_item_id: adj.inventory_item_id,
              quantity_change: adj.adjustment,
              reason: adj.reason
            })
          });
        } catch (invError) {
          console.error('Inventory adjustment failed:', invError);
        }
      }
      
      toast.success('Bill updated successfully');
      setEditBillDialogOpen(false);
      refreshAllTabs();
    } catch (error) {
      console.error('Failed to update bill:', error);
      toast.error('Failed to update bill');
    } finally {
      setEditBillLoading(false);
    }
  };

  const printReceipt = () => {
    if (!selectedBill) return;
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow pop-ups to print the receipt');
      return;
    }
    
    const servicesHtml = selectedBill.services?.length > 0 ? `
      <div class="section">
        <div class="section-header">SERVICES</div>
        <table>
          <thead>
            <tr>
              <th style="width: 50%">Description</th>
              <th style="width: 15%">Qty</th>
              <th style="width: 17.5%">Unit Price</th>
              <th style="width: 17.5%">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${selectedBill.services.map(s => `
              <tr>
                <td>${s.service_name}</td>
                <td class="center">${s.quantity}</td>
                <td class="right">$${s.unit_price.toFixed(2)}</td>
                <td class="right">$${s.total.toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
          <tfoot>
            <tr class="subtotal-row">
              <td colspan="3" class="right">Services Subtotal:</td>
              <td class="right"><strong>$${selectedBill.services_subtotal?.toFixed(2) || '0.00'}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>
    ` : '';
    
    const medicationsHtml = selectedBill.medications?.length > 0 ? `
      <div class="section">
        <div class="section-header">MEDICATIONS / SUPPLIES</div>
        <table>
          <thead>
            <tr>
              <th style="width: 50%">Description</th>
              <th style="width: 15%">Qty</th>
              <th style="width: 17.5%">Unit Price</th>
              <th style="width: 17.5%">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${selectedBill.medications.map(m => `
              <tr>
                <td>${m.medication_name}</td>
                <td class="center">${m.quantity}</td>
                <td class="right">$${m.unit_price.toFixed(2)}</td>
                <td class="right">$${m.total.toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
          <tfoot>
            <tr class="subtotal-row">
              <td colspan="3" class="right">Medications Subtotal:</td>
              <td class="right"><strong>$${selectedBill.medications_subtotal?.toFixed(2) || '0.00'}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>
    ` : '';
    
    const paymentsHtml = selectedBill.payments?.length > 0 ? `
      <div class="payments-section">
        <div class="payments-header">Payment History</div>
        ${selectedBill.payments.map(p => `
          <div class="payment-row">
            <span>${p.method.replace('_', ' ').toUpperCase()} ${p.reference ? `(Ref: ${p.reference})` : ''}</span>
            <span>$${p.amount.toFixed(2)}</span>
          </div>
        `).join('')}
      </div>
    ` : '';

    const statusBadge = selectedBill.balance_due <= 0 
      ? '<div class="status-badge paid">PAID IN FULL</div>'
      : `<div class="status-badge unpaid">BALANCE DUE: $${selectedBill.balance_due.toFixed(2)}</div>`;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice #${selectedBill.id?.slice(0, 8).toUpperCase()} - ${clinic?.name || 'Clinic'}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
            
            * { margin: 0; padding: 0; box-sizing: border-box; }
            
            body {
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
              font-size: 12px;
              color: #1f2937;
              line-height: 1.5;
              padding: 0;
              background: white;
            }
            
            .invoice-container {
              max-width: 800px;
              margin: 0 auto;
              padding: 40px;
            }
            
            /* Header */
            .header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              padding-bottom: 30px;
              border-bottom: 3px solid #0f766e;
              margin-bottom: 30px;
            }
            
            .logo-section img {
              height: 70px;
              margin-bottom: 10px;
            }
            
            .clinic-name {
              font-size: 24px;
              font-weight: 700;
              color: #0f766e;
              margin-bottom: 5px;
            }
            
            .clinic-info {
              color: #6b7280;
              font-size: 11px;
              line-height: 1.6;
            }
            
            .invoice-title {
              text-align: right;
            }
            
            .invoice-label {
              font-size: 28px;
              font-weight: 700;
              color: #0f766e;
              letter-spacing: 2px;
            }
            
            .invoice-number {
              font-size: 14px;
              color: #6b7280;
              margin-top: 5px;
            }
            
            .invoice-date {
              font-size: 12px;
              color: #9ca3af;
              margin-top: 3px;
            }
            
            /* Info Grid */
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 30px;
              margin-bottom: 30px;
            }
            
            .info-box {
              background: #f9fafb;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 20px;
            }
            
            .info-box-header {
              font-size: 10px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 1px;
              color: #0f766e;
              margin-bottom: 12px;
              padding-bottom: 8px;
              border-bottom: 1px solid #e5e7eb;
            }
            
            .info-row {
              margin-bottom: 6px;
            }
            
            .info-label {
              font-size: 10px;
              color: #9ca3af;
              text-transform: uppercase;
            }
            
            .info-value {
              font-size: 13px;
              color: #1f2937;
              font-weight: 500;
            }
            
            .patient-name {
              font-size: 16px;
              font-weight: 600;
              color: #1f2937;
              margin-bottom: 8px;
            }
            
            /* Sections */
            .section {
              margin-bottom: 25px;
            }
            
            .section-header {
              font-size: 11px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 1px;
              color: #0f766e;
              padding: 10px 0;
              border-bottom: 2px solid #0f766e;
              margin-bottom: 0;
            }
            
            /* Tables */
            table {
              width: 100%;
              border-collapse: collapse;
            }
            
            th {
              background: #f9fafb;
              padding: 12px 10px;
              text-align: left;
              font-size: 10px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              color: #6b7280;
              border-bottom: 1px solid #e5e7eb;
            }
            
            td {
              padding: 12px 10px;
              border-bottom: 1px solid #f3f4f6;
              font-size: 12px;
            }
            
            .center { text-align: center; }
            .right { text-align: right; }
            
            .subtotal-row td {
              padding-top: 15px;
              border-bottom: none;
              font-size: 12px;
            }
            
            /* Totals */
            .totals-section {
              margin-top: 30px;
              border-top: 2px solid #e5e7eb;
              padding-top: 20px;
            }
            
            .totals-grid {
              display: grid;
              grid-template-columns: 1fr 250px;
              gap: 30px;
            }
            
            .totals-box {
              background: #f9fafb;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 20px;
            }
            
            .total-row {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
              font-size: 13px;
            }
            
            .total-row.grand {
              font-size: 18px;
              font-weight: 700;
              color: #0f766e;
              border-top: 2px solid #0f766e;
              margin-top: 10px;
              padding-top: 15px;
            }
            
            .total-row.paid {
              color: #059669;
            }
            
            .total-row.due {
              color: #dc2626;
              font-weight: 600;
            }
            
            /* Status Badge */
            .status-badge {
              text-align: center;
              padding: 15px;
              border-radius: 8px;
              font-size: 14px;
              font-weight: 700;
              letter-spacing: 1px;
              margin-top: 15px;
            }
            
            .status-badge.paid {
              background: #ecfdf5;
              color: #059669;
              border: 2px solid #059669;
            }
            
            .status-badge.unpaid {
              background: #fef2f2;
              color: #dc2626;
              border: 2px solid #dc2626;
            }
            
            /* Payments */
            .payments-section {
              margin-top: 20px;
              padding: 15px;
              background: #f9fafb;
              border-radius: 8px;
            }
            
            .payments-header {
              font-size: 11px;
              font-weight: 600;
              text-transform: uppercase;
              color: #6b7280;
              margin-bottom: 10px;
            }
            
            .payment-row {
              display: flex;
              justify-content: space-between;
              padding: 5px 0;
              font-size: 11px;
              color: #4b5563;
            }
            
            /* Footer */
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #e5e7eb;
              text-align: center;
            }
            
            .footer-text {
              color: #9ca3af;
              font-size: 11px;
              margin-bottom: 5px;
            }
            
            .footer-thank {
              color: #0f766e;
              font-weight: 600;
              font-size: 14px;
            }
            
            @media print {
              body { padding: 0; }
              .invoice-container { padding: 20px; }
              @page { margin: 0.5in; size: letter; }
            }
          </style>
        </head>
        <body>
          <div class="invoice-container">
            <!-- Header -->
            <div class="header">
              <div class="logo-section">
                ${clinic?.logo_url ? `<img src="${clinic.logo_url}" alt="${clinic?.name || 'Clinic'}" />` : ''}
                <div class="clinic-name">${clinic?.name || 'Clinic'}</div>
                <div class="clinic-info">
                  ${selectedBill.location_name || ''}<br>
                  ${selectedBill.location_address || ''}<br>
                  ${selectedBill.location_phone ? `Tel: ${selectedBill.location_phone}` : ''}
                </div>
              </div>
              <div class="invoice-title">
                <div class="invoice-label">INVOICE</div>
                <div class="invoice-number">#${selectedBill.id?.slice(0, 8).toUpperCase()}</div>
                <div class="invoice-date">${new Date(selectedBill.created_at).toLocaleDateString('en-US', { 
                  year: 'numeric', month: 'long', day: 'numeric' 
                })}</div>
              </div>
            </div>
            
            <!-- Info Grid -->
            <div class="info-grid">
              <div class="info-box">
                <div class="info-box-header">Bill To</div>
                <div class="patient-name">${selectedBill.patient_name}</div>
                <div class="info-row">
                  <div class="info-label">MRN</div>
                  <div class="info-value">${selectedBill.patient_mrn || 'N/A'}</div>
                </div>
                ${selectedBill.patient_phone ? `
                  <div class="info-row">
                    <div class="info-label">Phone</div>
                    <div class="info-value">${selectedBill.patient_phone}</div>
                  </div>
                ` : ''}
                ${selectedBill.patient_email ? `
                  <div class="info-row">
                    <div class="info-label">Email</div>
                    <div class="info-value">${selectedBill.patient_email}</div>
                  </div>
                ` : ''}
                ${selectedBill.patient_address ? `
                  <div class="info-row">
                    <div class="info-label">Address</div>
                    <div class="info-value">${selectedBill.patient_address}</div>
                  </div>
                ` : ''}
              </div>
              
              <div class="info-box">
                <div class="info-box-header">Visit Details</div>
                <div class="info-row">
                  <div class="info-label">Visit Date</div>
                  <div class="info-value">${selectedBill.visit_date ? new Date(selectedBill.visit_date).toLocaleDateString() : new Date(selectedBill.created_at).toLocaleDateString()}</div>
                </div>
                ${selectedBill.patient_insurance_provider ? `
                  <div class="info-row" style="margin-top: 15px; padding-top: 10px; border-top: 1px solid #e5e7eb;">
                    <div class="info-label">Insurance Provider</div>
                    <div class="info-value">${selectedBill.patient_insurance_provider}</div>
                  </div>
                  ${selectedBill.patient_insurance_number ? `
                    <div class="info-row">
                      <div class="info-label">Policy Number</div>
                      <div class="info-value">${selectedBill.patient_insurance_number}</div>
                    </div>
                  ` : ''}
                ` : ''}
              </div>
            </div>
            
            <!-- Services -->
            ${servicesHtml}
            
            <!-- Medications -->
            ${medicationsHtml}
            
            <!-- Clinical Notes -->
            ${selectedBill.visit_notes ? `
              <div class="section">
                <div class="section-header">CLINICAL NOTES</div>
                <div style="padding: 15px; background: #f9fafb; border-radius: 0 0 8px 8px; font-size: 12px; color: #4b5563;">
                  ${selectedBill.visit_notes}
                </div>
              </div>
            ` : ''}
            
            <!-- Totals -->
            <div class="totals-section">
              <div class="totals-grid">
                <div>
                  ${paymentsHtml}
                </div>
                <div class="totals-box">
                  <div class="total-row">
                    <span>Services:</span>
                    <span>$${selectedBill.services_subtotal?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div class="total-row">
                    <span>Medications:</span>
                    <span>$${selectedBill.medications_subtotal?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div class="total-row">
                    <span>Subtotal:</span>
                    <span>$${selectedBill.subtotal?.toFixed(2) || '0.00'}</span>
                  </div>
                  ${selectedBill.discount > 0 ? `
                    <div class="total-row" style="color: #059669;">
                      <span>${selectedBill.discount_reason === 'Google Review Discount' ? 'Google Review Discount:' : 'Discount:'}</span>
                      <span>-$${selectedBill.discount.toFixed(2)}</span>
                    </div>
                  ` : ''}
                  ${selectedBill.tax_amount > 0 ? `
                    <div class="total-row">
                      <span>Tax (${selectedBill.tax_rate}%):</span>
                      <span>$${selectedBill.tax_amount.toFixed(2)}</span>
                    </div>
                  ` : ''}
                  <div class="total-row grand">
                    <span>TOTAL:</span>
                    <span>$${selectedBill.total?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div class="total-row paid">
                    <span>Amount Paid:</span>
                    <span>$${selectedBill.amount_paid?.toFixed(2) || '0.00'}</span>
                  </div>
                  ${selectedBill.balance_due > 0 ? `
                    <div class="total-row due">
                      <span>Balance Due:</span>
                      <span>$${selectedBill.balance_due.toFixed(2)}</span>
                    </div>
                  ` : ''}
                  ${statusBadge}
                </div>
              </div>
            </div>
            
            <!-- Footer -->
            <div class="footer">
              <div class="footer-thank">Thank you for choosing ${clinic?.name || 'our clinic'}!</div>
              <div class="footer-text">For billing inquiries, please contact our office.</div>
              <div class="footer-text" style="margin-top: 10px; font-size: 10px;">
                Invoice generated on ${new Date().toLocaleString()}
              </div>
            </div>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
    }, 800);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-slate-100 text-slate-600 border border-slate-200 px-2.5 py-0.5 rounded-full text-xs font-medium',
      partially_paid: 'bg-amber-100 text-amber-700 border border-amber-200 px-2.5 py-0.5 rounded-full text-xs font-medium',
      paid: 'bg-teal-100 text-teal-700 border border-teal-200 px-2.5 py-0.5 rounded-full text-xs font-medium',
      cancelled: 'bg-rose-100 text-rose-700 border border-rose-200 px-2.5 py-0.5 rounded-full text-xs font-medium'
    };
    return <span className={styles[status] || 'bg-slate-100 text-slate-600 px-2.5 py-0.5 rounded-full text-xs font-medium'}>{status.replace(/_/g, ' ')}</span>;
  };

  const getPaymentMethodIcon = (method) => {
    const icons = {
      cash: <Banknote className="w-4 h-4" />,
      card: <CreditCard className="w-4 h-4" />,
      insurance: <Building className="w-4 h-4" />,
      external_pos: <Smartphone className="w-4 h-4" />
    };
    return icons[method] || <DollarSign className="w-4 h-4" />;
  };

  // No longer filtering - data is fetched separately per tab
  // Use the state directly for each tab

  // Render bill accordion item
  const renderBillAccordion = (bill) => (
    <AccordionItem 
      key={bill.id} 
      value={bill.id}
      className="border rounded-lg px-4 bg-white shadow-sm"
      data-testid={`bill-row-${bill.id}`}
      data-bill-id={bill.id}
    >
      <AccordionTrigger className="hover:no-underline py-4">
        <div className="flex items-center justify-between w-full pr-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-slate-600">
                {bill.patient_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </span>
            </div>
            <div className="text-left">
              <p className="font-semibold text-slate-900 text-lg">
                B{bill.id?.slice(-8).toUpperCase()}
              </p>
              <p className="text-sm text-slate-500">
                {bill.patient_name} • {new Date(bill.created_at).toLocaleDateString()} {new Date(bill.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-semibold text-slate-900">${(bill.total || 0).toFixed(2)}</p>
              {(bill.balance_due || 0) > 0 && bill.status !== 'pending' && (
                <p className="text-xs text-slate-500">Due: ${(bill.balance_due || 0).toFixed(2)}</p>
              )}
            </div>
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <div className="pt-2 pb-4 border-t">
          {/* Patient & Visit Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="text-sm">
              <p className="text-slate-500">Patient</p>
              <p className="font-medium text-slate-900">{bill.patient_name}</p>
            </div>
            <div className="text-sm">
              <p className="text-slate-500">Doctor</p>
              <p className="font-medium text-slate-900">{bill.doctor_name || 'N/A'}</p>
            </div>
            <div className="text-sm">
              <p className="text-slate-500">Location</p>
              <p className="font-medium text-slate-900">{bill.location_name || 'N/A'}</p>
            </div>
            <div className="text-sm">
              <p className="text-slate-500">Visit Date</p>
              <p className="font-medium text-slate-900">{bill.visit_date ? `${new Date(bill.visit_date).toLocaleDateString()} ${new Date(bill.visit_date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}` : 'N/A'}</p>
            </div>
          </div>

          {/* Services - Always show */}
          <div className="mt-4">
            <p className="text-sm font-medium text-slate-700 mb-2">Services</p>
            {bill.services && bill.services.length > 0 ? (
              <div className="space-y-1">
                {bill.services.map((s, idx) => (
                  <div key={idx} className="flex justify-between text-sm py-2 px-3 bg-slate-50 rounded">
                    <div>
                      <span className="text-slate-900">{s.service_name || s.name}</span>
                      <span className="text-slate-500 ml-2">x{s.quantity || 1}</span>
                    </div>
                    <span className="font-medium text-slate-900">${(s.total || s.price || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 italic">No services</p>
            )}
          </div>

          {/* Medications - Always show */}
          <div className="mt-4">
            <p className="text-sm font-medium text-slate-700 mb-2">Medications</p>
            {bill.medications && bill.medications.length > 0 ? (
              <div className="space-y-1">
                {bill.medications.map((m, idx) => (
                  <div key={idx} className="flex justify-between text-sm py-2 px-3 bg-slate-50 rounded">
                    <div>
                      <span className="text-slate-900">{m.medication_name || m.name}</span>
                      <span className="text-slate-500 ml-2">x{m.quantity || 1}</span>
                    </div>
                    <span className="font-medium text-slate-900">${(m.total || (m.price || 0) * (m.quantity || 1)).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 italic">No medications</p>
            )}
          </div>

          {/* Billing Summary */}
          <div className="mt-4 p-4 bg-slate-50 rounded-lg">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-sm">
                <p className="text-slate-500">Subtotal</p>
                <p className="font-medium text-slate-900">${(bill.subtotal || bill.total || 0).toFixed(2)}</p>
              </div>
              {bill.discount > 0 && (
                <div className="text-sm">
                  <p className="text-slate-500 flex items-center gap-1">
                    {bill.discount_reason === 'Google Review Discount' && (
                      <svg className="w-3 h-3 shrink-0" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                    )}
                    {bill.discount_reason === 'Google Review Discount' ? 'Google Discount' : 'Discount'}
                  </p>
                  <p className="font-medium text-emerald-600">-${(bill.discount || 0).toFixed(2)}</p>
                </div>
              )}
              <div className="text-sm">
                <p className="text-slate-500">Total</p>
                <p className="font-semibold text-slate-900">${(bill.total || 0).toFixed(2)}</p>
              </div>
              <div className="text-sm">
                <p className="text-slate-500">Paid</p>
                <p className="font-medium text-teal-600">${(bill.amount_paid || 0).toFixed(2)}</p>
              </div>
              <div className="text-sm">
                <p className="text-slate-500">Balance Due</p>
                <p className={`font-semibold ${(bill.balance_due || 0) > 0 ? 'text-rose-600' : 'text-slate-900'}`}>
                  ${(bill.balance_due || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {/* Payment History */}
          {bill.payments && bill.payments.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-slate-700 mb-2">Payment History</p>
              <div className="space-y-1">
                {bill.payments.map((p, idx) => {
                  const editable = canEditPaymentMethod && isCollectedToday(p.paid_at);
                  const rowKey = `${bill.id}:${idx}`;
                  const isEditing = editingPayment === rowKey;
                  return (
                    <div key={idx} className="flex justify-between items-center text-sm py-2 px-3 bg-teal-50 rounded" data-testid={`payment-row-${idx}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        {getPaymentMethodIcon(p.method)}
                        {isEditing ? (
                          <div className="flex items-center gap-1" data-testid={`payment-method-editor-${idx}`}>
                            {['cash', 'card', 'zelle', 'insurance'].map((m) => (
                              <button
                                key={m}
                                type="button"
                                onClick={() => handleUpdatePaymentMethod(bill.id, idx, m)}
                                className={`px-2 py-1 rounded border text-xs capitalize transition-colors ${p.method === m ? 'border-teal-600 bg-teal-100 text-teal-700' : 'border-slate-300 text-slate-600 hover:border-teal-400'}`}
                                data-testid={`payment-method-option-${m}-${idx}`}
                              >
                                {m}
                              </button>
                            ))}
                            <button type="button" onClick={() => setEditingPayment(null)} className="px-2 py-1 text-xs text-slate-500 hover:text-slate-700" data-testid={`payment-method-cancel-${idx}`}>Cancel</button>
                          </div>
                        ) : (
                          <>
                            <span className="text-slate-700 capitalize">{p.method?.replace(/_/g, ' ')}</span>
                            {p.reference && <span className="text-slate-500">• Ref: {p.reference}</span>}
                            {editable && (
                              <button
                                type="button"
                                onClick={() => setEditingPayment(rowKey)}
                                className="inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700"
                                data-testid={`edit-payment-method-${idx}`}
                              >
                                <Pencil className="w-3 h-3" /> Edit
                              </button>
                            )}
                          </>
                        )}
                      </div>
                      <span className="font-medium text-teal-700">${(p.amount || 0).toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Revision History - For Front Desk to see doctor changes */}
          {bill.revisions && bill.revisions.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-amber-700 mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Doctor Revisions ({bill.revisions.length})
              </p>
              <div className="space-y-2">
                {bill.revisions.map((rev, idx) => (
                  <div key={idx} className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="text-sm font-medium text-amber-800">
                          Revision #{rev.revision_number} by {rev.revised_by_name}
                        </p>
                        <p className="text-xs text-amber-600">
                          {new Date(rev.revised_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">
                          ${rev.previous_total?.toFixed(2)} → ${rev.new_total?.toFixed(2)}
                        </p>
                        {rev.new_total !== rev.previous_total && (
                          <p className={`text-xs font-semibold ${rev.new_total > rev.previous_total ? 'text-rose-600' : 'text-emerald-600'}`}>
                            {rev.new_total > rev.previous_total ? '+' : ''}{(rev.new_total - rev.previous_total).toFixed(2)}
                          </p>
                        )}
                      </div>
                    </div>
                    {rev.reason && (
                      <p className="text-sm text-amber-700 bg-amber-100 p-2 rounded">
                        <span className="font-medium">Reason:</span> {rev.reason}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Visit Notes */}
          {bill.visit_notes && (
            <div className="mt-4">
              <p className="text-sm font-medium text-slate-700 mb-2">Notes</p>
              <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded">{bill.visit_notes}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 mt-4 pt-4 border-t">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => viewConsent(bill)}
              className="text-slate-700 border-slate-200 hover:bg-slate-50"
              data-testid={`view-consent-${bill.id}`}
            >
              <FileSignature className="w-4 h-4 mr-1" />
              View Consent
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => openReceiptDialog(bill)}
              className="text-slate-700 border-slate-200 hover:bg-slate-50"
              data-testid={`view-receipt-${bill.id}`}
            >
              <Eye className="w-4 h-4 mr-1" />
              View Receipt
            </Button>
            {(bill.status === 'pending' || bill.status === 'partially_paid') && (
              <Button 
                size="sm" 
                onClick={() => openPaymentDialog(bill)}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                data-testid={`pay-bill-${bill.id}`}
              >
                <DollarSign className="w-4 h-4 mr-1" />
                Process Payment
              </Button>
            )}
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="billing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Billing</h1>
          <p className="text-slate-500 text-sm mt-1">
            {workingLocation?.name || 'All Locations'} • Manage invoices and payments
          </p>
        </div>
        
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by patient or bill number..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            className="pl-9 w-64 bg-white border-slate-200"
            data-testid="billing-search"
          />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="pending" className="data-[state=active]:bg-white flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Pending
            {pendingTotalItems > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-slate-200 text-rose-600 rounded-full font-bold">
                {pendingTotalItems}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="partially_paid" className="data-[state=active]:bg-white flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Partially Paid
            {partiallyPaidTotalItems > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-slate-200 text-rose-600 rounded-full font-bold">
                {partiallyPaidTotalItems}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="paid" className="data-[state=active]:bg-white flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Paid
          </TabsTrigger>
        </TabsList>

        {/* Pending Tab */}
        <TabsContent value="pending" className="mt-6">
          {!tabsLoaded.pending || loadingPending ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner"></div>
            </div>
          ) : pendingBills.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="py-16">
                <div className="empty-state">
                  <Receipt className="empty-state-icon" />
                  <p className="empty-state-title">No pending bills</p>
                  <p className="empty-state-text">Bills waiting for payment will appear here</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <Accordion type="single" collapsible className="space-y-2" value={openAccordionId} onValueChange={setOpenAccordionId}>
                {pendingBills.map(renderBillAccordion)}
              </Accordion>
              {pendingTotalPages > 1 && (
                <div className="mt-4">
                  <DataPagination
                    currentPage={pendingCurrentPage}
                    totalPages={pendingTotalPages}
                    totalItems={pendingTotalItems}
                    pageSize={pendingPageSize}
                    onPageChange={setPendingCurrentPage}
                    onPageSizeChange={(size) => { setPendingPageSize(size); setPendingCurrentPage(1); }}
                  />
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* Partially Paid Tab */}
        <TabsContent value="partially_paid" className="mt-6">
          {!tabsLoaded.partially_paid || loadingPartiallyPaid ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner"></div>
            </div>
          ) : partiallyPaidBills.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="py-16">
                <div className="empty-state">
                  <Clock className="empty-state-icon" />
                  <p className="empty-state-title">No partially paid bills</p>
                  <p className="empty-state-text">Bills with partial payments will appear here</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <Accordion type="single" collapsible className="space-y-2" value={openAccordionId} onValueChange={setOpenAccordionId}>
                {partiallyPaidBills.map(renderBillAccordion)}
              </Accordion>
              {partiallyPaidTotalPages > 1 && (
                <div className="mt-4">
                  <DataPagination
                    currentPage={partiallyPaidCurrentPage}
                    totalPages={partiallyPaidTotalPages}
                    totalItems={partiallyPaidTotalItems}
                    pageSize={partiallyPaidPageSize}
                    onPageChange={setPartiallyPaidCurrentPage}
                    onPageSizeChange={(size) => { setPartiallyPaidPageSize(size); setPartiallyPaidCurrentPage(1); }}
                  />
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* Paid Tab */}
        <TabsContent value="paid" className="mt-6">
          {!tabsLoaded.paid || loadingPaid ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner"></div>
            </div>
          ) : paidBillsData.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="py-16">
                <div className="empty-state">
                  <CheckCircle className="empty-state-icon" />
                  <p className="empty-state-title">No paid bills</p>
                  <p className="empty-state-text">Completed payments will appear here</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <Accordion type="single" collapsible className="space-y-2" value={openAccordionId} onValueChange={setOpenAccordionId}>
                {paidBillsData.map(renderBillAccordion)}
              </Accordion>
              
              {/* Pagination for Paid Bills - show count always, controls only when multiple pages */}
              {paidTotalItems > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-200">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <span>Showing {((paidCurrentPage - 1) * paidPageSize) + 1} - {Math.min(paidCurrentPage * paidPageSize, paidTotalItems)} of {paidTotalItems} paid bills</span>
                    <select
                      value={paidPageSize}
                      onChange={(e) => {
                        setPaidPageSize(Number(e.target.value));
                        setPaidCurrentPage(1);
                      }}
                      className="ml-2 px-2 py-1 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                  </div>
                  
                  {paidTotalPages > 1 && (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPaidCurrentPage(1)}
                        disabled={paidCurrentPage === 1}
                        className="px-2"
                      >
                        <ChevronsLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPaidCurrentPage(p => Math.max(1, p - 1))}
                        disabled={paidCurrentPage === 1}
                        className="px-2"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      
                      <div className="flex items-center gap-1 mx-2">
                        {/* Page numbers */}
                        {Array.from({ length: Math.min(5, paidTotalPages) }, (_, i) => {
                          let pageNum;
                          if (paidTotalPages <= 5) {
                            pageNum = i + 1;
                          } else if (paidCurrentPage <= 3) {
                            pageNum = i + 1;
                          } else if (paidCurrentPage >= paidTotalPages - 2) {
                            pageNum = paidTotalPages - 4 + i;
                          } else {
                            pageNum = paidCurrentPage - 2 + i;
                          }
                          return (
                            <Button
                              key={pageNum}
                              variant={paidCurrentPage === pageNum ? "default" : "outline"}
                              size="sm"
                              onClick={() => setPaidCurrentPage(pageNum)}
                              className={`w-8 h-8 p-0 ${paidCurrentPage === pageNum ? 'bg-teal-600 text-white' : ''}`}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPaidCurrentPage(p => Math.min(paidTotalPages, p + 1))}
                        disabled={paidCurrentPage === paidTotalPages}
                        className="px-2"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPaidCurrentPage(paidTotalPages)}
                        disabled={paidCurrentPage === paidTotalPages}
                        className="px-2"
                      >
                        <ChevronsRight className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Payment Dialog with Discount Support - Professional Desktop Design */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 shrink-0">
            <DialogTitle className="text-xl font-semibold text-slate-900">
              {getEditableTotals().balanceDue <= 0 ? 'Close Bill' : 'Process Payment'}
            </DialogTitle>
            <p className="text-sm text-slate-500 mt-1">
              Invoice for <span className="font-medium text-slate-700">{selectedBill?.patient_name}</span> • Bill #B{selectedBill?.id?.slice(-8).toUpperCase()}
            </p>
          </div>
          
          {/* Zero Balance Bill - Show Close Bill UI */}
          {getEditableTotals().balanceDue <= 0 ? (
            <div className="flex flex-col flex-1 min-h-0">
              <DialogBody className="flex-1 overflow-y-auto px-6 py-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-green-800 mb-2">No Payment Required</h3>
                  <p className="text-green-700 mb-4">
                    This bill has a $0 balance. Click below to close the bill and mark the visit as complete.
                  </p>
                  <p className="text-2xl font-bold text-green-600">$0.00</p>
                  <p className="text-sm text-green-600 mt-1">Balance Due</p>
                </div>
                
                {/* Bill Summary for $0 bills */}
                <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Bill Summary</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Services:</span>
                      <span className="font-medium">${getEditableTotals().servicesTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Medications:</span>
                      <span className="font-medium">${getEditableTotals().medsTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between border-t border-slate-200 pt-1 mt-1">
                      <span className="text-slate-700 font-medium">Total:</span>
                      <span className="font-bold">${getEditableTotals().total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </DialogBody>
              
              {/* Footer for $0 bills */}
              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between shrink-0">
                <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)} className="px-6">
                  Cancel
                </Button>
                <Button 
                  type="button"
                  onClick={handleCloseBill}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 h-11" 
                  data-testid="close-bill-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Close Bill
                </Button>
              </div>
            </div>
          ) : (
          <form onSubmit={handlePayment} className="flex flex-col flex-1 min-h-0">
            <DialogBody className="flex-1 overflow-y-auto px-6 py-4">
              {/* Payment Section - Top */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
                {/* Google Review Discount */}
                <label className="flex items-center gap-2 cursor-pointer mb-4 pb-3 border-b border-slate-200" data-testid="google-review-discount-toggle">
                  <input
                    type="checkbox"
                    checked={googleReviewDiscount}
                    onChange={(e) => {
                      setGoogleReviewDiscount(e.target.checked);
                      // Recalculate payment amount after toggle
                      setTimeout(() => {
                        const sTotal = editableServices.reduce((s, i) => s + (i.total || 0), 0);
                        const mTotal = editableMedications.reduce((s, i) => s + (i.total || 0), 0);
                        const review = e.target.checked ? Math.min(10, sTotal + mTotal) : 0;
                        const paid = selectedBill?.amount_paid || 0;
                        const newBalance = Math.max(0, sTotal + mTotal - review - paid);
                        setPaymentForm(prev => ({ ...prev, amount: newBalance, cashAmount: prev.splitPayment ? newBalance : prev.cashAmount }));
                      }, 0);
                    }}
                    className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                  />
                  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <span className="text-sm font-medium text-slate-700">Google Review Discount</span>
                  <span className="text-sm font-semibold text-green-600 ml-auto">-$10.00</span>
                </label>

                {/* Balance Due */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-medium mb-1">Balance Due</p>
                    <p className="text-2xl font-bold text-red-600">${getEditableTotals().balanceDue.toFixed(2)}</p>
                  </div>
                  
                  {/* Split Payment Toggle */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={paymentForm.splitPayment}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          // Enable split payment - distribute balance to cash by default
                          setPaymentForm({ 
                            ...paymentForm, 
                            splitPayment: true,
                            cashAmount: getEditableTotals().balanceDue,
                            cardAmount: 0,
                            zelleAmount: 0
                          });
                        } else {
                          setPaymentForm({ 
                            ...paymentForm, 
                            splitPayment: false,
                            amount: getEditableTotals().balanceDue
                          });
                        }
                      }}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      data-testid="split-payment-toggle"
                    />
                    <span className="text-sm font-medium text-slate-700">Split Payment</span>
                  </label>
                </div>
                
                {!paymentForm.splitPayment ? (
                  /* Single Payment Mode */
                  <div className="flex flex-wrap items-end gap-4">
                    {/* Payment Amount */}
                    <div className="flex-1 min-w-[140px]">
                      <Label className="text-xs text-slate-500 uppercase font-medium">Pay Amount</Label>
                      <div className="relative mt-1">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="number"
                          step="0.01"
                          min="0.01"
                          max={getEditableTotals().balanceDue}
                          value={paymentForm.amount}
                          onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                          required
                          className="pl-9 h-10 text-lg font-semibold"
                          data-testid="payment-amount"
                        />
                      </div>
                    </div>
                    
                    {/* Payment Method */}
                    <div className="flex-shrink-0">
                      <Label className="text-xs text-slate-500 uppercase font-medium">Method</Label>
                      <div className="flex gap-1 mt-1">
                        {[
                          { value: 'cash', label: 'Cash', icon: Banknote },
                          { value: 'card', label: 'Card', icon: CreditCard },
                          { value: 'zelle', label: 'Zelle', icon: Smartphone },
                          { value: 'insurance', label: 'Ins', icon: Building }
                        ].map(({ value, label, icon: Icon }) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setPaymentForm({ ...paymentForm, method: value })}
                            className={`flex items-center gap-1 px-3 py-2 rounded-lg border-2 transition-all text-sm ${
                              paymentForm.method === value
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-200 hover:border-gray-300 text-gray-600'
                            }`}
                            data-testid={`payment-method-${value}`}
                          >
                            <Icon className="w-4 h-4" />
                            <span className="font-medium hidden sm:inline">{label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Reference */}
                    <div className="flex-1 min-w-[120px]">
                      <Label className="text-xs text-slate-500 uppercase font-medium">Reference</Label>
                      <Input
                        value={paymentForm.reference}
                        onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                        placeholder="Optional"
                        className="mt-1 h-10"
                        data-testid="payment-reference"
                      />
                    </div>
                  </div>
                ) : (
                  /* Split Payment Mode */
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      {/* Cash Amount */}
                      <div>
                        <Label className="text-xs text-slate-500 uppercase font-medium flex items-center gap-1">
                          <Banknote className="w-3 h-3" /> Cash
                        </Label>
                        <div className="relative mt-1">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={paymentForm.cashAmount}
                            onChange={(e) => setPaymentForm({ ...paymentForm, cashAmount: e.target.value })}
                            className="pl-9 h-10 font-semibold border-green-200 focus:border-green-400"
                            placeholder="0.00"
                            data-testid="split-cash-amount"
                          />
                        </div>
                      </div>
                      
                      {/* Card Amount */}
                      <div>
                        <Label className="text-xs text-slate-500 uppercase font-medium flex items-center gap-1">
                          <CreditCard className="w-3 h-3" /> Card
                        </Label>
                        <div className="relative mt-1">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500" />
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={paymentForm.cardAmount}
                            onChange={(e) => setPaymentForm({ ...paymentForm, cardAmount: e.target.value })}
                            className="pl-9 h-10 font-semibold border-blue-200 focus:border-blue-400"
                            placeholder="0.00"
                            data-testid="split-card-amount"
                          />
                        </div>
                      </div>
                      
                      {/* Zelle Amount */}
                      <div>
                        <Label className="text-xs text-slate-500 uppercase font-medium flex items-center gap-1">
                          <Smartphone className="w-3 h-3" /> Zelle
                        </Label>
                        <div className="relative mt-1">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-500" />
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={paymentForm.zelleAmount}
                            onChange={(e) => setPaymentForm({ ...paymentForm, zelleAmount: e.target.value })}
                            className="pl-9 h-10 font-semibold border-purple-200 focus:border-purple-400"
                            placeholder="0.00"
                            data-testid="split-zelle-amount"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Split Payment Summary */}
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                      <div className="flex items-center gap-4 text-sm">
                        {paymentForm.cashAmount > 0 && (
                          <span className="flex items-center gap-1 text-green-700">
                            <Banknote className="w-4 h-4" /> ${parseFloat(paymentForm.cashAmount || 0).toFixed(2)}
                          </span>
                        )}
                        {paymentForm.cardAmount > 0 && (
                          <span className="flex items-center gap-1 text-blue-700">
                            <CreditCard className="w-4 h-4" /> ${parseFloat(paymentForm.cardAmount || 0).toFixed(2)}
                          </span>
                        )}
                        {paymentForm.zelleAmount > 0 && (
                          <span className="flex items-center gap-1 text-purple-700">
                            <Smartphone className="w-4 h-4" /> ${parseFloat(paymentForm.zelleAmount || 0).toFixed(2)}
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-slate-500">Total Payment:</span>
                        <span className={`ml-2 text-lg font-bold ${
                          Math.abs(getTotalSplitAmount() - getEditableTotals().balanceDue) < 0.01 
                            ? 'text-green-600' 
                            : getTotalSplitAmount() > getEditableTotals().balanceDue 
                              ? 'text-red-600' 
                              : 'text-amber-600'
                        }`}>
                          ${getTotalSplitAmount().toFixed(2)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Validation Messages */}
                    {getTotalSplitAmount() > getEditableTotals().balanceDue && (
                      <p className="text-xs text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Total exceeds balance due by ${(getTotalSplitAmount() - getEditableTotals().balanceDue).toFixed(2)}
                      </p>
                    )}
                    {getTotalSplitAmount() < getEditableTotals().balanceDue && getTotalSplitAmount() > 0 && (
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Partial payment: ${(getEditableTotals().balanceDue - getTotalSplitAmount()).toFixed(2)} will remain due
                      </p>
                    )}
                    
                    {/* Reference field for split payments */}
                    <div>
                      <Label className="text-xs text-slate-500 uppercase font-medium">Reference (Optional)</Label>
                      <Input
                        value={paymentForm.reference}
                        onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                        placeholder="Transaction reference"
                        className="mt-1 h-10"
                        data-testid="split-payment-reference"
                      />
                    </div>
                  </div>
                )}
                
                {!paymentForm.splitPayment && paymentForm.amount < getEditableTotals().balanceDue && paymentForm.amount > 0 && (
                  <p className="text-xs text-amber-600 mt-3 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Partial payment: ${(getEditableTotals().balanceDue - paymentForm.amount).toFixed(2)} will remain due
                  </p>
                )}
              </div>

              {/* Bill Items Section - Scrollable */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Bill Items</h3>
                
                {/* Services Table */}
                {editableServices.length > 0 && (
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex justify-between items-center">
                      <span className="text-xs font-semibold text-slate-600 uppercase">Services ({editableServices.length})</span>
                      <span className="text-sm font-medium text-slate-700">${editableServices.reduce((sum, s) => sum + (s.total || 0), 0).toFixed(2)}</span>
                    </div>
                    <div className="max-h-[200px] overflow-y-auto">
                      <table className="w-full text-sm table-fixed">
                        <thead className="sticky top-0 bg-white">
                          <tr className="border-b border-slate-100">
                            <th className="text-left px-4 py-2 font-medium text-slate-500 w-[50%]">Item</th>
                            <th className="text-center px-2 py-2 font-medium text-gray-500 w-[20%]">Qty</th>
                            <th className="text-right px-4 py-2 font-medium text-gray-500 w-[30%]">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {editableServices.map((s, idx) => (
                            <tr key={idx} className="border-b border-gray-50 last:border-0">
                              <td className="px-4 py-2 w-[50%]">
                                <p className="font-medium text-gray-900 truncate">{s.service_name}</p>
                                <p className="text-xs text-gray-400">${(s.unit_price || 0).toFixed(2)} each</p>
                              </td>
                              <td className="px-2 py-2 text-center text-gray-600 w-[20%]">{s.quantity}</td>
                              <td className="px-4 py-2 text-right w-[30%]">
                                <p className="font-semibold text-gray-900">${(s.total || 0).toFixed(2)}</p>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Medications Table */}
                {editableMedications.length > 0 && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
                      <span className="text-xs font-semibold text-gray-600 uppercase">Medications ({editableMedications.length})</span>
                      <span className="text-sm font-medium text-slate-700">${editableMedications.reduce((sum, m) => sum + (m.total || 0), 0).toFixed(2)}</span>
                    </div>
                    <div className="max-h-[200px] overflow-y-auto">
                      <table className="w-full text-sm table-fixed">
                        <thead className="sticky top-0 bg-white">
                          <tr className="border-b border-gray-100">
                            <th className="text-left px-4 py-2 font-medium text-gray-500 w-[50%]">Item</th>
                            <th className="text-center px-2 py-2 font-medium text-gray-500 w-[20%]">Qty</th>
                            <th className="text-right px-4 py-2 font-medium text-gray-500 w-[30%]">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {editableMedications.map((m, idx) => (
                            <tr key={idx} className="border-b border-gray-50 last:border-0">
                              <td className="px-4 py-2 w-[50%]">
                                <p className="font-medium text-gray-900 truncate">{m.medication_name}</p>
                                <p className="text-xs text-gray-400">${(m.unit_price || 0).toFixed(2)} each</p>
                              </td>
                              <td className="px-2 py-2 text-center text-gray-600 w-[20%]">{m.quantity}</td>
                              <td className="px-4 py-2 text-right w-[30%]">
                                <p className="font-semibold text-gray-900">${(m.total || 0).toFixed(2)}</p>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Totals Summary - Compact */}
                {getEditableTotals().alreadyPaid > 0 && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Total:</span>
                        <span className="font-medium text-gray-900 ml-1">${getEditableTotals().total.toFixed(2)}</span>
                      </div>
                      {getEditableTotals().alreadyPaid > 0 && (
                        <div>
                          <span className="text-gray-500">Already Paid:</span>
                          <span className="font-medium text-green-600 ml-1">-${getEditableTotals().alreadyPaid.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </DialogBody>
            
            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between shrink-0">
              <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)} className="px-6">
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 h-11" 
                data-testid="confirm-payment-btn"
                disabled={isProcessingPayment || (paymentForm.splitPayment ? getTotalSplitAmount() <= 0 || getTotalSplitAmount() > getEditableTotals().balanceDue : false)}
              >
                <DollarSign className="w-4 h-4 mr-2" />
                {isProcessingPayment ? 'Processing...' : `Process $${paymentForm.splitPayment ? getTotalSplitAmount().toFixed(2) : (paymentForm.amount || '0.00')} Payment`}
              </Button>
            </div>
          </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={receiptDialogOpen} onOpenChange={setReceiptDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <div className="receipt print:p-0" id="receipt-content">
            {/* Header */}
            <div className="receipt-header border-b-2 border-slate-300 pb-6 mb-6">
              {clinic?.logo_url && (
                <img 
                  src={clinic.logo_url} 
                  alt={clinic?.name || 'Clinic'} 
                  className="receipt-logo h-16 mx-auto mb-3 object-contain"
                />
              )}
              <h2 className="receipt-title text-2xl font-bold">{clinic?.name || 'Clinic'}</h2>
              <p className="text-slate-600 font-medium">{selectedBill?.location_name}</p>
              {selectedBill?.location_address && (
                <p className="text-slate-500 text-sm">{selectedBill.location_address}</p>
              )}
              {selectedBill?.location_phone && (
                <p className="text-slate-500 text-sm">Tel: {selectedBill.location_phone}</p>
              )}
            </div>

            {/* Receipt Number and Date */}
            <div className="flex justify-between items-start mb-6 pb-4 border-b border-slate-200">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Receipt Number</p>
                <p className="font-mono font-bold text-lg">#{selectedBill?.id?.slice(0, 8).toUpperCase()}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-400 uppercase tracking-wider">Date</p>
                <p className="font-medium">{selectedBill && new Date(selectedBill.created_at).toLocaleDateString('en-US', { 
                  year: 'numeric', month: 'long', day: 'numeric' 
                })}</p>
                <p className="text-sm text-slate-500">{selectedBill && new Date(selectedBill.created_at).toLocaleTimeString()}</p>
              </div>
            </div>
            
            {/* Patient and Visit Info */}
            <div className="grid grid-cols-2 gap-6 mb-6 p-4 bg-stone-50 rounded-lg">
              <div>
                <h3 className="text-xs text-stone-400 uppercase tracking-wider mb-2">Patient Information</h3>
                <p className="font-semibold text-stone-900">{selectedBill?.patient_name}</p>
                {selectedBill?.patient_mrn && (
                  <p className="text-sm text-stone-600">MRN: {selectedBill.patient_mrn}</p>
                )}
                {selectedBill?.patient_phone && (
                  <p className="text-sm text-stone-600">Phone: {selectedBill.patient_phone}</p>
                )}
                {selectedBill?.patient_email && (
                  <p className="text-sm text-stone-600">Email: {selectedBill.patient_email}</p>
                )}
                {selectedBill?.patient_address && (
                  <p className="text-sm text-stone-600">Address: {selectedBill.patient_address}</p>
                )}
              </div>
              <div>
                <h3 className="text-xs text-stone-400 uppercase tracking-wider mb-2">Visit Information</h3>
                {selectedBill?.visit_date && (
                  <p className="text-sm text-stone-600">
                    <span className="font-medium">Visit Date:</span> {new Date(selectedBill.visit_date).toLocaleDateString()}
                  </p>
                )}
                {(selectedBill?.patient_insurance_provider || selectedBill?.patient_insurance_number) && (
                  <div className="mt-2 pt-2 border-t border-stone-200">
                    <p className="text-xs text-stone-400 uppercase tracking-wider">Insurance</p>
                    {selectedBill?.patient_insurance_provider && (
                      <p className="text-sm text-stone-600">{selectedBill.patient_insurance_provider}</p>
                    )}
                    {selectedBill?.patient_insurance_number && (
                      <p className="text-sm text-stone-600">Policy: {selectedBill.patient_insurance_number}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Services */}
            {selectedBill?.services?.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-stone-900 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-teal-600 rounded-full"></span>
                  Services Rendered
                </h3>
                <table className="receipt-table w-full">
                  <thead>
                    <tr className="border-b-2 border-stone-200">
                      <th className="text-left py-2 text-xs text-stone-500 uppercase">Description</th>
                      <th className="text-right py-2 text-xs text-stone-500 uppercase">Qty</th>
                      <th className="text-right py-2 text-xs text-stone-500 uppercase">Unit Price</th>
                      <th className="text-right py-2 text-xs text-stone-500 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBill?.services?.map((s, idx) => (
                      <tr key={idx} className="border-b border-stone-100">
                        <td className="py-2">
                          {s.service_name}
                          {s.notes && <p className="text-xs text-stone-400 mt-0.5">{s.notes}</p>}
                        </td>
                        <td className="text-right py-2">{s.quantity}</td>
                        <td className="text-right py-2">${s.unit_price.toFixed(2)}</td>
                        <td className="text-right py-2 font-medium">${s.total.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3" className="text-right py-2 font-medium text-stone-600">Services Subtotal:</td>
                      <td className="text-right py-2 font-semibold">${selectedBill?.services_subtotal?.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* Medications */}
            {selectedBill?.medications?.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-stone-900 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-emerald-600 rounded-full"></span>
                  Medications / Supplies
                </h3>
                <table className="receipt-table w-full">
                  <thead>
                    <tr className="border-b-2 border-stone-200">
                      <th className="text-left py-2 text-xs text-stone-500 uppercase">Description</th>
                      <th className="text-right py-2 text-xs text-stone-500 uppercase">Qty</th>
                      <th className="text-right py-2 text-xs text-stone-500 uppercase">Unit Price</th>
                      <th className="text-right py-2 text-xs text-stone-500 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBill?.medications?.map((m, idx) => (
                      <tr key={idx} className="border-b border-stone-100">
                        <td className="py-2">
                          {m.medication_name}
                          {m.notes && <p className="text-xs text-stone-400 mt-0.5">{m.notes}</p>}
                        </td>
                        <td className="text-right py-2">{m.quantity}</td>
                        <td className="text-right py-2">${m.unit_price.toFixed(2)}</td>
                        <td className="text-right py-2 font-medium">${m.total.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3" className="text-right py-2 font-medium text-stone-600">Medications Subtotal:</td>
                      <td className="text-right py-2 font-semibold">${selectedBill?.medications_subtotal?.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* Visit Notes */}
            {selectedBill?.visit_notes && (
              <div className="mb-6 p-4 bg-stone-50 rounded-lg">
                <h3 className="text-xs text-stone-400 uppercase tracking-wider mb-2">Clinical Notes</h3>
                <p className="text-sm text-stone-700">{selectedBill.visit_notes}</p>
              </div>
            )}

            {/* Totals */}
            <div className="border-t-2 border-stone-300 pt-4 mt-6">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-stone-600">Services Subtotal:</span>
                  <span>${selectedBill?.services_subtotal?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-stone-600">Medications Subtotal:</span>
                  <span>${selectedBill?.medications_subtotal?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="flex justify-between font-medium border-t border-stone-200 pt-2">
                  <span>Subtotal:</span>
                  <span>${selectedBill?.subtotal?.toFixed(2)}</span>
                </div>
                {selectedBill?.discount > 0 && (
                  <div className="flex justify-between items-center text-emerald-600">
                    <span className="flex items-center gap-1.5">
                      {selectedBill?.discount_reason === 'Google Review Discount' && (
                        <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                      )}
                      {selectedBill?.discount_reason === 'Google Review Discount' ? 'Google Review Discount:' : 'Discount:'}
                    </span>
                    <span>-${selectedBill?.discount.toFixed(2)}</span>
                  </div>
                )}
                {selectedBill?.tax_amount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>Tax ({selectedBill?.tax_rate}%):</span>
                    <span>${selectedBill?.tax_amount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xl font-bold border-t-2 border-stone-900 pt-3 mt-2">
                  <span>TOTAL:</span>
                  <span>${selectedBill?.total?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-emerald-600 font-medium">
                  <span>Amount Paid:</span>
                  <span>${selectedBill?.amount_paid?.toFixed(2)}</span>
                </div>
                {selectedBill?.balance_due > 0 && (
                  <div className="flex justify-between text-rose-600 font-bold text-lg bg-rose-50 p-2 rounded">
                    <span>BALANCE DUE:</span>
                    <span>${selectedBill?.balance_due.toFixed(2)}</span>
                  </div>
                )}
                {selectedBill?.balance_due <= 0 && (
                  <div className="flex justify-center text-emerald-600 font-bold text-lg bg-emerald-50 p-2 rounded mt-2">
                    <span>✓ PAID IN FULL</span>
                  </div>
                )}
              </div>
            </div>

            {/* Payment History */}
            {selectedBill?.payments?.length > 0 && (
              <div className="mt-6 pt-4 border-t border-stone-200">
                <h3 className="font-semibold text-stone-900 mb-3 text-sm">Payment History</h3>
                <div className="space-y-2">
                  {selectedBill.payments.map((p, idx) => (
                    <div key={idx} className="flex justify-between items-center text-sm p-2 bg-stone-50 rounded">
                      <span className="flex items-center gap-2">
                        {getPaymentMethodIcon(p.method)}
                        <span className="capitalize">{p.method.replace('_', ' ')}</span>
                        {p.reference && <span className="text-stone-400 text-xs">Ref: {p.reference}</span>}
                      </span>
                      <div className="text-right">
                        <span className="font-medium">${p.amount.toFixed(2)}</span>
                        <p className="text-xs text-stone-400">{new Date(p.paid_at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-stone-200 text-center text-xs text-stone-400">
              <p className="font-medium text-stone-600 mb-1">Thank you for choosing {clinic?.name || 'our clinic'}!</p>
              <p>For questions about this bill, please contact us at the clinic.</p>
              <p className="mt-2">This receipt was generated on {new Date().toLocaleString()}</p>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-4 no-print">
            <Button variant="outline" onClick={() => setReceiptDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={printReceipt} className="bg-teal-700 hover:bg-teal-800">
              <Printer className="w-4 h-4 mr-2" />
              Print Receipt
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Consent Dialog */}
      <ViewConsentDialog
        open={consentDialogOpen}
        onOpenChange={setConsentDialogOpen}
        consent={selectedConsent}
        patientName={selectedBill?.patient_name}
      />

      {/* Edit Bill Dialog */}
      <Dialog open={editBillDialogOpen} onOpenChange={setEditBillDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Bill
            </DialogTitle>
            <DialogDescription>
              Modify bill items. Inventory will be automatically adjusted for medication changes.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-4">
            {/* Patient Info */}
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="font-medium text-slate-900">{selectedBill?.patient_name}</p>
              <p className="text-sm text-slate-500">
                {selectedBill?.visit_date && new Date(selectedBill.visit_date).toLocaleDateString()}
                {selectedBill?.status && (
                  <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                    selectedBill.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                    selectedBill.status === 'partially_paid' ? 'bg-amber-100 text-amber-700' :
                    'bg-rose-100 text-rose-700'
                  }`}>
                    {selectedBill.status.replace('_', ' ').toUpperCase()}
                  </span>
                )}
              </p>
            </div>

            {/* Services */}
            <div>
              <h4 className="font-medium text-slate-900 mb-2">Services</h4>
              {editBillForm.services.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No services</p>
              ) : (
                <div className="space-y-2">
                  {editBillForm.services.map((s, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-slate-900 text-sm">{s.service_name}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <Label className="text-xs text-slate-500">Qty:</Label>
                          <Input
                            type="number"
                            min="0"
                            value={s.quantity}
                            onChange={(e) => updateEditBillService(idx, 'quantity', e.target.value)}
                            className="w-16 h-8 text-sm text-center"
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <Label className="text-xs text-slate-500">$</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={s.unit_price}
                            onChange={(e) => updateEditBillService(idx, 'unit_price', e.target.value)}
                            className="w-20 h-8 text-sm"
                          />
                        </div>
                        <span className="text-sm font-semibold text-slate-900 w-20 text-right">
                          ${(s.total || 0).toFixed(2)}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeEditBillService(idx)}
                          className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 p-1 h-8 w-8"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Medications */}
            <div>
              <h4 className="font-medium text-slate-900 mb-2">Medications</h4>
              <p className="text-xs text-amber-600 mb-2">
                ⚠️ Changing medication quantities will adjust inventory automatically
              </p>
              {editBillForm.medications.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No medications</p>
              ) : (
                <div className="space-y-2">
                  {editBillForm.medications.map((m, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-slate-900 text-sm">{m.medication_name}</p>
                        {m.original_quantity !== m.quantity && (
                          <p className="text-xs text-amber-600">
                            Original: {m.original_quantity} → New: {m.quantity}
                            {m.quantity > m.original_quantity ? ' (+' + (m.quantity - m.original_quantity) + ' will be deducted)' : ' (+' + (m.original_quantity - m.quantity) + ' will be restored)'}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <Label className="text-xs text-slate-500">Qty:</Label>
                          <Input
                            type="number"
                            min="0"
                            value={m.quantity}
                            onChange={(e) => updateEditBillMedication(idx, 'quantity', e.target.value)}
                            className="w-16 h-8 text-sm text-center"
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <Label className="text-xs text-slate-500">$</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={m.unit_price}
                            onChange={(e) => updateEditBillMedication(idx, 'unit_price', e.target.value)}
                            className="w-20 h-8 text-sm"
                          />
                        </div>
                        <span className="text-sm font-semibold text-slate-900 w-20 text-right">
                          ${(m.total || 0).toFixed(2)}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeEditBillMedication(idx)}
                          className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 p-1 h-8 w-8"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Totals Summary */}
            <div className="bg-slate-100 p-4 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Services Subtotal:</span>
                <span className="font-medium">${getEditBillTotals().servicesTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Medications Subtotal:</span>
                <span className="font-medium">${getEditBillTotals().medsTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold border-t border-slate-300 pt-2 mt-2">
                <span>New Total:</span>
                <span>${getEditBillTotals().total.toFixed(2)}</span>
              </div>
              {selectedBill?.amount_paid > 0 && (
                <>
                  <div className="flex justify-between text-sm text-emerald-600">
                    <span>Already Paid:</span>
                    <span>-${selectedBill.amount_paid.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-rose-600">
                    <span>New Balance Due:</span>
                    <span>${Math.max(0, getEditBillTotals().total - selectedBill.amount_paid).toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditBillDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEditedBill}
              disabled={editBillLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {editBillLoading ? (
                <>
                  <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Billing;

